import React, { useState, useRef, useEffect } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useUserContext } from 'talent_matching/contexts/user_context';
import AppliedFilterChip, {
  AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import { AppliedOnType, CompanyNameFilter } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import AppliedAdvancedFilterChip from 'talent_matching/components/applied_filters/applied_advanced_filter_chip';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { formatShortDate } from 'talent_search/lib/utils';

const t = withLocalePrefix('talent_matching.filters.applied');

const MAX_COLLAPSED_HEIGHT = 100; // Approximately 3 lines of chips (28px height + 8px gap per row)

const APPLIED_ON_TEXT_MAPPING: {
  [key in AppliedOnType]: (start: string, end: string) => string;
} = {
  [AppliedOnType.TODAY]: () => t('today'),
  [AppliedOnType.YESTERDAY]: () => t('yesterday'),
  [AppliedOnType.LAST_SEVEN_DAYS]: () => t('last_seven_days'),
  [AppliedOnType.BETWEEN]: (start, end) => t('between', { start, end }),
  [AppliedOnType.ON_OR_AFTER]: (start) => t('on_or_after', { start }),
  [AppliedOnType.ON_OR_BEFORE]: (_start, end) => t('on_or_before', { end }),
};

const AppliedFiltersRow = () => {
  const { getValues } = useFormContext();
  const { canUseLocations, userDateFormat } = useUserContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [visibleChipCount, setVisibleChipCount] = useState<number | null>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const [
    advancedFilters,
    appliedOnEnd,
    appliedOnStart,
    appliedOnType,
    jobPosts,
    excludePreviouslyRejected,
    feedbackStatus,
    locationFilterType,
    debouncedLocationName,
    geolocation,
    geolocationRadius,
    manualReview,
    priorityCandidates,
    savedForLater,
    excludeHighRisk,
    otherActiveApplications,
    minExperience,
    maxExperience,
    companyNameFilters,
    canHave,
    mustHave,
    doesNotHave,
    questionFilters,
    educationFilters,
  ] = getValues([
    'advancedFilters',
    'appliedOnEnd',
    'appliedOnStart',
    'appliedOnType',
    'jobPosts',
    'excludePreviouslyRejected',
    'feedbackStatus',
    'locationFilterType',
    'debouncedLocationName',
    'geolocation',
    'geolocationRadius',
    'manualReview',
    'priorityCandidates',
    'savedForLater',
    'excludeHighRisk',
    'otherActiveApplications',
    'minExperience',
    'maxExperience',
    'companyNameFilters',
    'canHave',
    'mustHave',
    'doesNotHave',
    'questionFilters',
    'educationFilters',
  ]);

  const buildAppliedFilters = (): AppliedFilterChipProps[] => {
    const filters: AppliedFilterChipProps[] = [];

    if (priorityCandidates) {
      filters.push({
        label: t('priority_candidates'),
        filterKeys: ['priorityCandidates'],
        uniqueKey: t('priority_candidates'),
      });
    }

    if (manualReview) {
      filters.push({
        label: t('manual_review'),
        filterKeys: ['manualReview'],
        uniqueKey: t('manual_review'),
      });
    }

    if (savedForLater) {
      filters.push({
        label: t('saved_for_later'),
        filterKeys: ['savedForLater'],
        uniqueKey: t('saved_for_later'),
      });
    }

    if (feedbackStatus !== SCORECARD_STATUS.ALL) {
      filters.push({
        label: t(`feedback_status_${feedbackStatus}`),
        filterKeys: ['feedbackStatus'],
        uniqueKey: t(`feedback_status_${feedbackStatus}`),
      });
    }

    if (excludeHighRisk) {
      filters.push({
        label: t('exclude_high_risk'),
        filterKeys: ['excludeHighRisk'],
        uniqueKey: t('exclude_high_risk'),
      });
    }

    if (otherActiveApplications) {
      filters.push({
        label: t(
          otherActiveApplications === '1'
            ? 'other_active_applications_include'
            : 'other_active_applications_exclude'
        ),
        filterKeys: ['otherActiveApplications'],
        uniqueKey:
          otherActiveApplications === '1'
            ? t('other_active_applications_include')
            : t('other_active_applications_exclude'),
      });
    }

    if (appliedOnType && (appliedOnStart || appliedOnEnd)) {
      const startDateText =
        formatShortDate(appliedOnStart, userDateFormat) || '...';
      const endDateText =
        formatShortDate(appliedOnEnd, userDateFormat) || '...';
      const whenText = APPLIED_ON_TEXT_MAPPING[appliedOnType as AppliedOnType](
        startDateText,
        endDateText
      );

      filters.push({
        label: t('applied_on', { when: whenText }),
        filterKeys: ['appliedOnType', 'appliedOnStart', 'appliedOnEnd'],
        uniqueKey: 'applied_on',
      });
    }

    if (jobPosts.length > 0) {
      const jobPostNames = jobPosts
        .map((item: Item) => item.label)
        .filter(Boolean)
        .join(', ');

      filters.push({
        label: t('job_post_name', { job_posts: jobPostNames }),
        filterKeys: ['jobPosts'],
        uniqueKey: 'job_post_name',
      });
    }

    if (canUseLocations) {
      if (locationFilterType === 'radius' && geolocation) {
        const locationText = t('location', {
          radius: geolocationRadius,
          location: geolocation.city,
        });
        filters.push({
          label: t('located', { location: locationText }),
          filterKeys: ['geolocation', 'geolocationRadius'],
          uniqueKey: 'located',
        });
      }

      if (locationFilterType === 'name' && debouncedLocationName) {
        filters.push({
          label: t('located_in', { location: debouncedLocationName }),
          filterKeys: ['locationName'],
          uniqueKey: 'located_in',
        });
      }
    }

    if (excludePreviouslyRejected) {
      filters.push({
        label: t('exclude_previously_rejected'),
        filterKeys: ['excludePreviouslyRejected'],
        uniqueKey: t('exclude_previously_rejected'),
      });
    }

    if (minExperience) {
      const yearsText = t('years', { count: Number(minExperience) });
      filters.push({
        label: t('minimum_experience', { years: yearsText }),
        filterKeys: ['minExperience'],
        uniqueKey: 'minimum_experience',
      });
    }

    if (maxExperience) {
      const yearsText = t('years', { count: Number(maxExperience) });
      filters.push({
        label: t('maximum_experience', { years: yearsText }),
        filterKeys: ['maxExperience'],
        uniqueKey: 'maximum_experience',
      });
    }

    if (companyNameFilters.length > 0) {
      companyNameFilters.forEach((filter: CompanyNameFilter) => {
        if (filter.value) {
          filters.push({
            label: t(`company_name_${filter.mode}`, { company: filter.value }),
            filterKeys: [`companyNameFilters.${filter.id}`],
            uniqueKey: `companyNameFilters.${filter.id}`,
          });
        }
      });
    }

    if (canHave.length > 0) {
      filters.push({
        label: t('keywords.can_have', { keywords: canHave.join(', ') }),
        filterKeys: ['canHave'],
        uniqueKey: 'keywords.can_have',
      });
    }

    if (mustHave.length > 0) {
      filters.push({
        label: t('keywords.must_have', { keywords: mustHave.join(', ') }),
        filterKeys: ['mustHave'],
        uniqueKey: 'keywords.must_have',
      });
    }

    if (doesNotHave.length > 0) {
      filters.push({
        label: t('keywords.does_not_have', {
          keywords: doesNotHave.join(', '),
        }),
        filterKeys: ['doesNotHave'],
        uniqueKey: 'keywords.does_not_have',
      });
    }

    return filters;
  };

  const buildAdvancedFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    advancedFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });
    return filters;
  };

  const buildQuestionFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    questionFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });

    return filters;
  };

  const buildEducationFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    educationFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });

    return filters;
  };

  const appliedFilters = buildAppliedFilters();
  const appliedAdvancedFilters = buildAdvancedFilters();
  const appliedQuestionFilters = buildQuestionFilters();
  const appliedEducationFilters = buildEducationFilters();

  const allChips = [
    ...appliedFilters.map((filter, index) => ({
      type: 'filter' as const,
      key: filter.uniqueKey,
      index,
      component: <AppliedFilterChip key={filter.uniqueKey} {...filter} />,
    })),
    ...appliedAdvancedFilters.map((filter, index) => ({
      type: 'advanced' as const,
      key: filter.reactKey,
      index,
      component: (
        <AppliedAdvancedFilterChip
          filter={filter}
          key={filter.reactKey}
          fieldName="advancedFilters"
        />
      ),
    })),
    ...appliedQuestionFilters.map((filter, index) => ({
      type: 'question' as const,
      key: filter.reactKey,
      index,
      component: (
        <AppliedAdvancedFilterChip
          filter={filter}
          key={filter.reactKey}
          fieldName="questionFilters"
        />
      ),
    })),
    ...appliedEducationFilters.map((filter, index) => ({
      type: 'education' as const,
      key: filter.reactKey,
      index,
      component: (
        <AppliedAdvancedFilterChip
          filter={filter}
          key={filter.reactKey}
          fieldName="educationFilters"
        />
      ),
    })),
  ];

  useEffect(() => {
    // Reset visibleChipCount when chips change so it recalculates
    setVisibleChipCount(null);
    setIsExpanded(false);
  }, [allChips.length]);

  useEffect(() => {
    // This useEffect makes it possible to display only the first 3 rows of chips
    // while preserving the Tooltip behavior. Using overflow: hidden is preferred
    // but not possible because the tooltips are not displayed or cut off by this.
    if (!measureRef.current || visibleChipCount !== null) {
      return;
    }

    let rafId: number;
    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(() => {
        if (measureRef.current) {
          const children = Array.from(measureRef.current.children);
          let count = 0;

          for (let i = 0; i < children.length; i++) {
            const child = children[i] as HTMLElement;
            const rect = child.getBoundingClientRect();
            const childBottom =
              rect.top +
              rect.height -
              measureRef.current.getBoundingClientRect().top;

            if (childBottom > MAX_COLLAPSED_HEIGHT) {
              break;
            }
            count++;
          }

          setVisibleChipCount(count);
          setNeedsExpansion(count < allChips.length);
        }
      });
    }, 0);

    return () => {
      clearTimeout(timer);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [allChips.length, visibleChipCount]);

  if (allChips.length === 0) {
    return null;
  }

  const chipsToShow =
    isExpanded || visibleChipCount === null
      ? allChips
      : allChips.slice(0, visibleChipCount);

  return (
    <Stack spacing={1} sx={{ marginTop: 2 }}>
      {/* Hidden measurement container */}
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          visibility: visibleChipCount === null ? 'visible' : 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '8px',
          width: stackRef.current?.offsetWidth || '100%',
        }}
      >
        {allChips.map((chip) => chip.component)}
      </div>

      <Stack
        ref={stackRef}
        spacing={1}
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        sx={{ opacity: visibleChipCount === null ? 0 : 1 }}
      >
        {chipsToShow.map((chip) => chip.component)}
      </Stack>

      {needsExpansion && (
        <Stack
          alignItems="start"
          sx={{ opacity: visibleChipCount === null ? 0 : 1 }}
        >
          <TertiaryButton size="sm" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? t('show_less') : t('show_more')}
          </TertiaryButton>
        </Stack>
      )}
    </Stack>
  );
};

export default AppliedFiltersRow;
